import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * One Angular build serves two brands: Automaeta (company) on the marketing
 * routes, Kytelos (product) on /v2. A single static <link rel="icon"> can only
 * be right for one of them, so we swap it on navigation.
 *
 * Uses the *micro* cuts deliberately — the full marks are too intricate to read
 * at 16-32px. See 03 - Infrastructure/08 in the vault.
 */
@Injectable({ providedIn: 'root' })
export class BrandFaviconService {
  private readonly doc = inject(DOCUMENT);
  private readonly router = inject(Router);

  private static readonly ICONS = {
    kytelos:   'assets/brand/kytelos-favicon.svg',
    automaeta: 'assets/brand/automaeta-favicon.svg',
  } as const;

  private current: string | null = null;

  init(): void {
    this.apply(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.apply(e.urlAfterRedirects));
  }

  private apply(url: string): void {
    const brand = url.startsWith('/v2') ? 'kytelos' : 'automaeta';
    const href = BrandFaviconService.ICONS[brand];
    if (href === this.current) { return; }
    this.current = href;

    // Replace rather than mutate: some browsers ignore an href change on an
    // already-parsed icon link.
    this.doc.head.querySelectorAll('link[rel~="icon"]').forEach(n => n.remove());
    const link = this.doc.createElement('link');
    link.setAttribute('rel', 'icon');
    link.setAttribute('type', 'image/svg+xml');
    link.setAttribute('href', href);
    this.doc.head.appendChild(link);

    this.doc.title = brand === 'kytelos' ? 'Kytelos' : 'Automaeta';
  }
}
